import { useCallback, useMemo } from 'react';
import { useLanguage } from './useLanguage';
import { normalizeLanguageAliasCode, normalizeLanguageCode, normalizeLanguageMap, toLanguageAliasKey } from '@/lib/language';
function buildAliases(availableLanguages) {
    const aliases = {};
    availableLanguages.forEach((lang) => {
        const iso2 = normalizeLanguageCode(lang.isoCode);
        if (!iso2) {
            return;
        }
        aliases[toLanguageAliasKey(lang.isoCode)] = iso2;
        const iso3Alias = toLanguageAliasKey(lang.isoCode3);
        if (iso3Alias) {
            aliases[iso3Alias] = iso2;
        }
    });
    ['en', 'it', 'de', 'fr', 'es', 'eng', 'ita', 'ger', 'deu', 'fra', 'fre', 'spa'].forEach((alias) => {
        const normalized = normalizeLanguageAliasCode(alias);
        if (normalized) {
            aliases[toLanguageAliasKey(alias)] = normalized;
        }
    });
    return aliases;
}
function normalizeTranslationInput(multiLangStr, aliases) {
    if (typeof multiLangStr === 'string') {
        return {};
    }
    const entries = {};
    Object.entries(multiLangStr).forEach(([key, value]) => {
        if (typeof value !== 'string') {
            return;
        }
        const trimmed = value.trim();
        if (!trimmed) {
            return;
        }
        entries[key] = value;
    });
    return normalizeLanguageMap(entries, aliases);
}
export const useTranslation = () => {
    const { currentLanguage, availableLanguages } = useLanguage();
    const aliases = useMemo(() => buildAliases(availableLanguages), [availableLanguages]);
    const t = useCallback((multiLangStr, fallbackLang = 'en') => {
        if (typeof multiLangStr === 'string') {
            return multiLangStr;
        }
        const normalizedMap = normalizeTranslationInput(multiLangStr, aliases);
        const fallbackIso2 = normalizeLanguageCode(fallbackLang) ?? aliases[toLanguageAliasKey(fallbackLang)] ?? 'en';
        return normalizedMap[currentLanguage]
            || normalizedMap[fallbackIso2]
            || normalizedMap.en
            || normalizedMap.it
            || Object.values(normalizedMap)[0]
            || '';
    }, [aliases, currentLanguage]);
    const tForLang = useCallback((multiLangStr, langCode) => {
        if (typeof multiLangStr === 'string') {
            return multiLangStr;
        }
        const normalizedMap = normalizeTranslationInput(multiLangStr, aliases);
        const normalizedLang = normalizeLanguageCode(langCode) ?? aliases[toLanguageAliasKey(langCode)] ?? null;
        if (normalizedLang && normalizedMap[normalizedLang]) {
            return normalizedMap[normalizedLang];
        }
        return normalizedMap.en
            || normalizedMap.it
            || Object.values(normalizedMap)[0]
            || '';
    }, [aliases]);
    const hasTranslation = useCallback((multiLangStr, langCode) => {
        if (typeof multiLangStr === 'string') {
            return true;
        }
        const normalizedMap = normalizeTranslationInput(multiLangStr, aliases);
        const targetLanguage = langCode
            ? (normalizeLanguageCode(langCode) ?? aliases[toLanguageAliasKey(langCode)] ?? null)
            : currentLanguage;
        if (!targetLanguage) {
            return false;
        }
        return targetLanguage in normalizedMap;
    }, [aliases, currentLanguage]);
    const getAllTranslations = useCallback((multiLangStr) => {
        if (typeof multiLangStr === 'string') {
            return { [currentLanguage]: multiLangStr };
        }
        return normalizeTranslationInput(multiLangStr, aliases);
    }, [aliases, currentLanguage]);
    return {
        t,
        tForLang,
        hasTranslation,
        getAllTranslations,
        currentLanguage,
    };
};
